/**
 * ── THE ASSET HANDLE — the type (plan §4.2) and its state machine ────────────────────────────────
 *
 * `acquire()` returns one of these SYNCHRONOUSLY, whatever the network does, so the caller always
 * has something to hold and release. The machine is `queued → fetching → parsing → ready | failed
 * | released`; `ready`, `failed` and `released` are settled and never move again except
 * `ready → released`. Three contracts a caller may rely on:
 *
 *  · `ready` NEVER REJECTS. It resolves to a `Loaded` whose `ok` is false on failure, carrying a
 *    `HandleFailure` (`unsupported | missing | network | budget | aborted`). Failure is a state,
 *    never an exception (rule 6) — a `.catch` on this promise is dead code.
 *  · `instance()` is a FRESH CLONE of the shared, refcounted SOURCE (a parsed GLB, a `Texture`),
 *    bound to this handle: `release()` detaches every instance this handle minted, because after
 *    the source's grace period its GPU objects are disposed and an instance left in the graph
 *    would draw from freed buffers. Before `ready` — or after `failed`/`released` — it returns
 *    `null` and warns ONCE; the caller's placeholder policy (§4.2) is what shows then.
 *  · `release()` is IDEMPOTENT. Before `ready` it aborts the fetch (the `staticMesh` abort-on-
 *    dispose precedent) and the handle settles `aborted`; after `ready` it decrements the source
 *    refcount and the ledger's grace period starts.
 *
 * The service owns fetch, parse, dispose and the ledger; this module owns only the per-handle
 * arithmetic — which is why it is driven through a CONTROLLER (`createHandle`) rather than
 * mutated from outside: every transition is one named verb with one guard, and a handle whose
 * state disagrees with its promise is unrepresentable.
 *
 * NOT pure (it names three's types) but three is imported as TYPES ONLY: the runtime cost of this
 * module is a closure per handle.
 */

import type * as THREE from 'three/webgpu';

import type { AssetKind, MaterialArchetype } from './core/catalog';
import type { AssetLane, FailureCode, HandleFailure, HandleState } from './core/types';
import type { SurfaceMaterial } from '../render/materials/surfaceFactory';

// ── Sources and instances, per kind ────────────────────────────────────────────────────────────

/** What a disposal walks — the `AxonRig.dispose()` pattern (`axonRig.ts:413-429`), generalised. */
export interface TrackedResources {
  geometries: Set<THREE.BufferGeometry>;
  materials: Set<THREE.Material>;
  textures: Set<THREE.Texture>;
}

/** `rig.json` beside a body (plan §5.1). v1 carries it as data; P2 gives it a schema. */
export type RigSidecar = Record<string, unknown>;

export interface MeshSource {
  kind: 'mesh';
  scene: THREE.Object3D;
  /** `true` when the GLB carries a `SkinnedMesh` — instances then go through `SkeletonUtils.clone`. */
  skinned: boolean;
  tracked: TrackedResources;
}

export interface BodySource {
  kind: 'body';
  scene: THREE.Object3D;
  rig: RigSidecar;
  tracked: TrackedResources;
}

export interface ClipSource {
  kind: 'clip';
  /** SHARED, never cloned: an `AnimationClip` is immutable data a mixer binds per root. */
  clips: readonly THREE.AnimationClip[];
}

export interface TextureSource {
  kind: 'texture';
  /** SHARED: one upload, N materials. The refcount is at the source (§4.2). */
  texture: THREE.Texture;
}

/**
 * ⭐ A MATERIAL SOURCE — the ONE kind whose content is not bytes (plan §13.4).
 *
 * `KIND_REQUIRED_ROLES.material` is `[]`, so a material asset has NO blob: its definition is
 * catalog data (`archetype`, `params`, `textures`) and its content is the resolved `extends`
 * chain plus the TEXTURE ASSETS that chain names. So this source is composed from the asset's
 * DEPENDENCIES rather than from its own transfer, which is why the service builds it where the
 * deps have landed rather than where blobs are folded.
 *
 * ⚠️ `material` IS SHARED, EXACTLY AS A `Texture` IS, AND THAT IS RULE 7 TAKEN TO ITS LIMIT.
 * `makeSurface` is called ONCE per `(id, hash)`; every `instance()` hands back the same object, so
 * N meshes wearing this asset are N draws against ONE material, ONE cache key and ONE program. A
 * fresh `MeshStandardNodeMaterial` per `instance()` would be N render objects and N sets of
 * bindings for a graph that is identical by construction — and nothing would ever dispose them,
 * because a `Material` has no `removeFromParent` for `handle.release()` to reach.
 */
export interface MaterialSource {
  kind: 'material';
  /** Never null: a chain that names no archetype fails `unsupported` instead of composing. */
  archetype: MaterialArchetype;
  /** The folded params (archetype defaults, then each ancestor, then the asset) as handed to the factory. */
  params: Readonly<Record<string, unknown>>;
  /** Factory slot → the TEXTURE ASSET ID the chain resolved. These ids are this asset's derived deps. */
  slots: Readonly<Record<string, string>>;
  /** Slots whose texture arrived and is bound on {@link material}. */
  bound: readonly string[];
  /**
   * Slots whose texture did NOT arrive: the archetype's own default stands in its place and the
   * service warned once (rule 6). Never a throw, and never a magenta material — an unbound `map`
   * on `surf.opaque` is the factory's flat `color`, which is a surface, not an error signal.
   */
  degraded: readonly string[];
  /** The chain actually folded, NEAREST FIRST — `[id, parent, grandparent, …]`. */
  chain: readonly string[];
  /** SHARED. See the warning above. */
  material: SurfaceMaterial;
}

/**
 * ⭐ AN ENTITY SOURCE — THE CODE-BEARING KIND, CARRYING NO CODE (plan §13.6).
 *
 * `KIND_REQUIRED_ROLES.entity` is `[]`, so like a material this asset has NO blob: its definition
 * is the catalog's `entity` block. What it holds is a REGISTRY CLASS NAME and its params — the
 * behaviour is a lazy chunk the runtime already shipped (ADR-0001: deploy whole, download partial),
 * so what streams is the prefab and its DATA, never the code.
 *
 * ⚠️ THERE IS NO THREE OBJECT HERE AT ALL, AND THAT IS THE ARCHITECTURE RATHER THAN AN OMISSION.
 * `src/assets/**` may not import `src/entities/**` (ADR-0001, `tests/entityBoundary.test.ts`), so
 * the service cannot construct an `EntityInstance` and must not try. It RESOLVES the definition;
 * the SPAWN goes through `EntityHost` in the app layer, which is why `instance()` hands back a
 * PLACEMENT — the same `{ type, …params }` shape a world's `entities[]` row carries, which the
 * host already knows how to validate, lazy-load and construct. One shape, one host, no second
 * spawn path.
 */
export interface EntitySource {
  kind: 'entity';
  /** A registry class name. Gated at ACQUIRE through `requires.classes` — never resolved here. */
  class: string;
  /** The prefab's params. A placement's own fields still win over these (ADR-0002 precedence). */
  params: Readonly<Record<string, unknown>>;
  /** The data assets held resident for as long as this handle is — the entry's derived deps. */
  data: readonly string[];
}

/**
 * What `instance()` hands back for an `entity`: one world-placement row, freshly built. It is
 * DATA, so there is nothing to detach on release and nothing to dispose — the LIFETIME this handle
 * owns is the prefab's data assets, not the instance the host makes from this object.
 */
export interface EntityPlacement extends Record<string, unknown> {
  type: string;
}

export type AssetSource = MeshSource | BodySource | ClipSource | TextureSource | MaterialSource | EntitySource;

export type SourceOf<K extends AssetKind> = K extends 'mesh'
  ? MeshSource
  : K extends 'body'
    ? BodySource
    : K extends 'clip'
      ? ClipSource
      : K extends 'texture'
        ? TextureSource
        : K extends 'material'
          ? MaterialSource
          : K extends 'entity'
            ? EntitySource
            : never;

export interface BodyInstance {
  /** A skeleton-preserving clone (`SkeletonUtils.clone`) — the mixer and the pose controller bind to THIS root. */
  root: THREE.Object3D;
  rig: RigSidecar;
}

export type Instance<K extends AssetKind> = K extends 'mesh'
  ? THREE.Object3D
  : K extends 'body'
    ? BodyInstance
    : K extends 'clip'
      ? readonly THREE.AnimationClip[]
      : K extends 'texture'
        ? THREE.Texture
        : K extends 'material'
          ? SurfaceMaterial
          : K extends 'entity'
            ? EntityPlacement
            : never;

// ── The handle ─────────────────────────────────────────────────────────────────────────────────

export interface Loaded<K extends AssetKind = AssetKind> {
  ok: boolean;
  id: string;
  kind: K;
  /** The catalog's content hash, or `null` when the id was not in the catalog. */
  hash: string | null;
  failure: HandleFailure | null;
  /** The shared source (present iff `ok`). Prefer `handle.instance()`; read this only to inspect. */
  source: SourceOf<K> | null;
}

export interface AssetHandle<K extends AssetKind = AssetKind> {
  readonly id: string;
  readonly kind: K;
  readonly lane: AssetLane;
  readonly hash: string | null;
  readonly state: HandleState;
  /** NEVER rejects — see the header. */
  readonly ready: Promise<Loaded<K>>;
  /** 0..1, step-based (`queued` 0 · `fetching` 0.25 · `parsing` 0.75 · settled 1). */
  readonly progress: number;
  readonly failure?: HandleFailure;
  /** A fresh clone bound to this handle; `null` (warned once) unless `state === 'ready'`. */
  instance(): Instance<K> | null;
  /** Idempotent. Before `ready`: aborts. After: decrements the source. Always: detaches instances. */
  release(): void;
}

/** The service's side of a handle. Every transition is a verb here; nothing else writes state. */
export interface HandleController<K extends AssetKind = AssetKind> {
  readonly handle: AssetHandle<K>;
  readonly settled: boolean;
  readonly released: boolean;
  /** Progress the machine forward (never backward, never past a settled state). */
  advance(state: 'queued' | 'fetching' | 'parsing'): void;
  /** `→ ready`. Ignored once settled. */
  settle(source: SourceOf<K>): void;
  /** `→ failed`. Ignored once settled. */
  fail(code: FailureCode, message: string): void;
  /** `→ released`: detaches instances; an unsettled handle settles `aborted` first. */
  markReleased(): void;
}

export interface HandleInit<K extends AssetKind> {
  id: string;
  kind: K;
  lane: AssetLane;
  hash: string | null;
  /** Per-kind clone (`instantiate.ts`) — injected so this module stays three-free at runtime. */
  instantiate: (source: SourceOf<K>) => Instance<K>;
  /** The service's release path (ledger, scheduler, abort). Called at most once, BEFORE `markReleased`. */
  onRelease: (ctrl: HandleController<K>) => void;
  warnOnce: (key: string, message: string) => void;
}

const PROGRESS: Record<HandleState, number> = {
  queued: 0,
  fetching: 0.25,
  parsing: 0.75,
  ready: 1,
  failed: 1,
  released: 1,
};

const ORDER: Record<'queued' | 'fetching' | 'parsing', number> = { queued: 0, fetching: 1, parsing: 2 };

type Detachable = { removeFromParent(): unknown };

const isDetachable = (v: unknown): v is Detachable =>
  v !== null && typeof v === 'object' && typeof (v as { removeFromParent?: unknown }).removeFromParent === 'function';

export function createHandle<K extends AssetKind>(init: HandleInit<K>): HandleController<K> {
  let state: HandleState = 'queued';
  let source: SourceOf<K> | null = null;
  let failure: HandleFailure | null = null;
  let settled = false;
  let released = false;
  let releasing = false;
  const instances: Detachable[] = [];

  let resolveReady!: (loaded: Loaded<K>) => void;
  const ready = new Promise<Loaded<K>>((res) => {
    resolveReady = res;
  });

  const finish = (ok: boolean): void => {
    settled = true;
    resolveReady({ ok, id: init.id, kind: init.kind, hash: init.hash, failure, source });
  };

  const handle: AssetHandle<K> = {
    id: init.id,
    kind: init.kind,
    lane: init.lane,
    hash: init.hash,
    get state() {
      return state;
    },
    ready,
    get progress() {
      return PROGRESS[state];
    },
    get failure() {
      return failure ?? undefined;
    },
    instance() {
      if (state !== 'ready' || source === null) {
        init.warnOnce(
          `instance:${init.id}:${state}`,
          `[assets] instance() on '${init.id}' while ${state} — returning null (use the kind's placeholder)`,
        );
        return null;
      }
      const inst = init.instantiate(source);
      // A mesh instance is an Object3D; a body's is `{ root }`. Clips and textures are shared data
      // with nothing to detach.
      if (isDetachable(inst)) instances.push(inst);
      else if (inst !== null && typeof inst === 'object' && isDetachable((inst as { root?: unknown }).root)) {
        instances.push((inst as { root: Detachable }).root);
      }
      return inst;
    },
    release() {
      if (released || releasing) return;
      releasing = true;
      init.onRelease(ctrl);
      ctrl.markReleased();
    },
  };

  const ctrl: HandleController<K> = {
    handle,
    get settled() {
      return settled;
    },
    get released() {
      return released;
    },
    advance(next) {
      if (settled) return;
      if (state === 'queued' || state === 'fetching' || state === 'parsing') {
        if (ORDER[next] > ORDER[state]) state = next;
      }
    },
    settle(src) {
      if (settled) return;
      source = src;
      state = 'ready';
      finish(true);
    },
    fail(code, message) {
      if (settled) return;
      failure = { code, message };
      state = 'failed';
      finish(false);
    },
    markReleased() {
      if (released) return;
      released = true;
      for (const inst of instances) inst.removeFromParent();
      instances.length = 0;
      if (!settled) {
        failure = { code: 'aborted', message: `'${init.id}' released before it was ready` };
        state = 'released';
        finish(false);
      } else if (state === 'ready') {
        // The ONE legal move out of a settled state. A `failed` handle stays `failed`: its
        // failure is the fact a reader wants, and "released" would erase it.
        state = 'released';
      }
      // The clone factory must not hand out instances of a source that may be disposed.
      source = null;
    },
  };

  return ctrl;
}
